import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Star, ArrowLeft, Flag, ChatCircleDots, CheckCircle, Warning, Lock,
} from "@phosphor-icons/react";

/**
 * "Le mie recensioni" lato agente — pagina dashboard.
 *   • Lista recensioni ricevute (tutte)
 *   • Risposta inline (1 sola, non modificabile)
 *   • Bottone "Segnala" → invia al founder
 *   • Card stato qualità in alto: REA + n.immobili + opt-in
 */
export default function MieRecensioni() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avg, setAvg] = useState(null);
  const [count, setCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [rRev, rQual] = await Promise.all([
        api.get("/reviews/mine"),
        api.get("/users/me/quality-status"),
      ]);
      setReviews(rRev.data?.reviews || []);
      setAvg(rRev.data?.average_stars);
      setCount(rRev.data?.count || 0);
      setQuality(rQual.data);
    } catch (err) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onReply = async (id, text) => {
    if (!text || text.length < 2) return;
    try {
      await api.post(`/reviews/${id}/reply`, { text });
      toast.success("Risposta pubblicata");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const onFlag = async (id, reason) => {
    if (!reason || reason.length < 5) {
      toast.error("Scrivi una ragione (min. 5 caratteri)");
      return;
    }
    try {
      await api.post(`/reviews/${id}/flag`, { reason });
      toast.success("Segnalata. Verrà rivista entro 24h.");
      load();
    } catch (err) {
      toast.error("Errore segnalazione");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1C1C1A]" data-testid="mie-recensioni-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Indietro
        </button>

        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
          <h1 className="font-editorial text-[28px] sm:text-[34px] leading-tight">Le mie recensioni</h1>
          {avg ? (
            <div className="text-right">
              <div className="font-editorial text-[28px] text-[#1C1C1A] leading-none tabular-nums">
                <span className="text-[#D97706]">★</span> {avg}
              </div>
              <div className="text-[11px] text-[#6B6A65] uppercase tracking-[0.16em] font-semibold mt-0.5">
                su {count}
              </div>
            </div>
          ) : null}
        </div>
        <p className="text-[13px] text-[#6B6A65] leading-snug max-w-2xl">
          Recensioni dei proprietari che ti hanno scelto su Stima Casa. Più stelle accumuli, più alto compari nella griglia agenti pubblica.
        </p>

        {/* Card stato qualità */}
        {quality && <QualityCard quality={quality} onReload={load} />}

        {/* Recensioni */}
        {loading && (
          <div className="mt-10 text-center">
            <div className="inline-block w-8 h-8 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && reviews.length === 0 && (
          <div className="mt-8 bg-white border border-[#EAE7DF] rounded-2xl p-8 text-center">
            <Star size={32} weight="duotone" className="text-[#C8C5BD] mx-auto" />
            <h3 className="font-editorial text-[19px] mt-3 text-[#1C1C1A]">Nessuna recensione ancora</h3>
            <p className="mt-2 text-[13px] text-[#6B6A65] max-w-md mx-auto leading-snug">
              Quando un cliente ti contatta da Stima Casa, riceve dopo 7 giorni un'email che gli chiede di lasciare una stella. Le sue recensioni appariranno qui.
            </p>
          </div>
        )}

        {!loading && reviews.length > 0 && (
          <div className="mt-6 space-y-3">
            {reviews.map((r) => (
              <ReviewRow key={r.id} review={r} onReply={onReply} onFlag={onFlag} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QualityCard({ quality, onReload }) {
  const [reaCode, setReaCode] = useState(quality.rea_code || "");
  const [busy, setBusy] = useState(false);

  const submitRea = async () => {
    if (!reaCode || reaCode.length < 4) {
      toast.error("Inserisci un numero REA valido");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users/me/rea", { rea_code: reaCode });
      toast.success("REA inviato — verifica entro 24h");
      onReload();
    } catch (err) {
      toast.error("Errore invio REA");
    } finally {
      setBusy(false);
    }
  };

  const togglePublic = async (enabled) => {
    try {
      await api.post("/users/me/public-profile", { enabled });
      toast.success(enabled ? "Profilo attivato" : "Profilo disattivato");
      onReload();
    } catch (err) {
      toast.error("Errore");
    }
  };

  return (
    <div className="mt-5 bg-white border border-[#EAE7DF] rounded-2xl p-5 sm:p-6" data-testid="quality-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#5A6A4D] mb-3">
        {quality.is_public ? <CheckCircle size={14} weight="fill" className="text-[#7C8B6E]" /> : <Lock size={14} weight="fill" className="text-[#D97706]" />}
        {quality.is_public ? "Profilo pubblico attivo" : "Profilo pubblico non ancora attivo"}
      </div>
      <h2 className="font-editorial text-[20px] text-[#1C1C1A] mb-1">
        {quality.is_public ? "Stai ricevendo richieste da Stima Casa" : "Sblocca le richieste da Stima Casa"}
      </h2>
      <p className="text-[13px] text-[#3F3D38] leading-snug">
        {quality.is_public
          ? "Compari nella griglia degli agenti della tua città. I proprietari possono sceglierti dopo la stima."
          : "Per apparire ai proprietari della tua città servono 3 requisiti. Vediamo cosa manca:"}
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RequirementBox
          ok={quality.requirements.rea_verified}
          pending={quality.requirements.rea_submitted && !quality.requirements.rea_verified}
          label="REA verificato"
          sub={
            quality.rea_status === "verified" ? `Codice ${quality.rea_code}` :
            quality.rea_status === "pending" ? `${quality.rea_code} in verifica` :
            "Inserisci il numero REA della tua CCIAA"
          }
        />
        <RequirementBox
          ok={quality.requirements.listings_min_3}
          label="Almeno 3 immobili"
          sub={`Hai ${quality.listings_count} ${quality.listings_count === 1 ? "immobile" : "immobili"} attivi`}
        />
        <RequirementBox
          ok={quality.requirements.public_opt_in}
          label="Profilo opt-in"
          sub={quality.requirements.public_opt_in ? "Attivo" : "Disattivato dall'agente"}
        />
      </div>

      {!quality.requirements.rea_verified && quality.rea_status !== "pending" && (
        <div className="mt-5 p-4 bg-[#FAFAF7] border border-[#EAE7DF] rounded-lg">
          <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-2">
            Inserisci il tuo numero REA CCIAA
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={reaCode}
              onChange={(e) => setReaCode(e.target.value.toUpperCase())}
              placeholder="Es. MI-1234567"
              className="flex-1 px-3.5 py-2.5 text-[14px] border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] bg-white"
              data-testid="rea-input"
            />
            <button
              onClick={submitRea}
              disabled={busy}
              className="px-5 py-2.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[13.5px] font-semibold transition-colors disabled:opacity-60"
              data-testid="rea-submit"
            >
              {busy ? "Invio…" : "Invia per verifica"}
            </button>
          </div>
          <p className="mt-2 text-[11.5px] text-[#9A968B]">
            Verifichiamo manualmente entro 24h sul Registro Imprese CCIAA. Filtra dilettanti, garantisce qualità a chi cerca un agente serio.
          </p>
        </div>
      )}

      {quality.requirements.rea_verified && quality.requirements.listings_min_3 && (
        <div className="mt-5 flex items-center gap-2 text-[12.5px] text-[#3F3D38] border-t border-[#EAE7DF] pt-4">
          <span>Apparizione su Stima Casa:</span>
          <button
            onClick={() => togglePublic(!quality.requirements.public_opt_in)}
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              quality.requirements.public_opt_in
                ? "bg-[#7C8B6E] text-white hover:bg-[#5A6A4D]"
                : "bg-[#EAE7DF] text-[#3F3D38] hover:bg-[#D8D5CC]"
            }`}
            data-testid="public-toggle"
          >
            {quality.requirements.public_opt_in ? "Attiva" : "Disattiva"}
          </button>
          <span className="text-[#9A968B] text-[11.5px] italic">
            (puoi sospendere temporaneamente se sei in ferie)
          </span>
        </div>
      )}
    </div>
  );
}

function RequirementBox({ ok, pending, label, sub }) {
  return (
    <div className={`border rounded-lg p-3 ${
      ok ? "bg-[#F1F4ED] border-[#7C8B6E]/30" :
      pending ? "bg-[#FEF3C7] border-[#D97706]/30" :
      "bg-white border-[#EAE7DF]"
    }`}>
      <div className="flex items-center gap-1.5">
        {ok ? <CheckCircle size={14} weight="fill" className="text-[#7C8B6E]" /> :
         pending ? <Warning size={14} weight="fill" className="text-[#D97706]" /> :
         <span className="w-3.5 h-3.5 rounded-full border-2 border-[#C8C5BD]" />}
        <div className="text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#3F3D38]">{label}</div>
      </div>
      <div className="text-[12px] text-[#6B6A65] mt-1 leading-snug">{sub}</div>
    </div>
  );
}

function ReviewRow({ review, onReply, onFlag }) {
  const [replyText, setReplyText] = useState("");
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [showFlagBox, setShowFlagBox] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const date = review.created_at ? new Date(review.created_at).toLocaleDateString("it-IT") : "";

  return (
    <div className="bg-white border border-[#EAE7DF] rounded-xl p-4 sm:p-5" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map((n) => (
                <Star key={n} size={15} weight={review.stars >= n ? "fill" : "regular"} className={review.stars >= n ? "text-[#D97706]" : "text-[#C8C5BD]"} />
              ))}
            </div>
            <span className="text-[11.5px] text-[#6B6A65]">· {date}</span>
            {review.hidden_by_admin && (
              <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#E11D48] bg-[#FEE2E2] px-2 py-0.5 rounded">
                Nascosta
              </span>
            )}
            {review.flagged_by_agent && !review.hidden_by_admin && (
              <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#D97706] bg-[#FEF3C7] px-2 py-0.5 rounded">
                Segnalata
              </span>
            )}
          </div>
          <div className="font-editorial text-[16px] text-[#1C1C1A]">{review.author_name}</div>
          {review.city && <div className="text-[11.5px] text-[#9A968B]">{review.city}</div>}
        </div>
      </div>
      {review.text && (
        <p className="mt-3 text-[13.5px] text-[#3F3D38] leading-relaxed italic">
          "{review.text}"
        </p>
      )}

      {review.agent_reply ? (
        <div className="mt-4 pl-4 border-l-3 border-[#7C8B6E]">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#5A6A4D] mb-1">La tua risposta</div>
          <p className="text-[13px] text-[#3F3D38] leading-relaxed">{review.agent_reply.text}</p>
        </div>
      ) : (
        <div className="mt-4">
          {!showReplyBox && !showFlagBox && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowReplyBox(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[12px] font-semibold transition-colors"
                data-testid={`review-reply-btn-${review.id}`}
              >
                <ChatCircleDots size={13} weight="fill" /> Rispondi
              </button>
              {!review.flagged_by_agent && (
                <button
                  onClick={() => setShowFlagBox(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-[#EAE7DF] hover:border-[#E11D48] text-[#6B6A65] hover:text-[#E11D48] text-[12px] font-semibold transition-colors"
                  data-testid={`review-flag-btn-${review.id}`}
                >
                  <Flag size={13} weight="fill" /> Segnala
                </button>
              )}
            </div>
          )}
          {showReplyBox && (
            <div className="mt-2 space-y-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="La tua risposta (1 sola, non modificabile)…"
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 text-[13px] border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] bg-white"
                data-testid={`review-reply-text-${review.id}`}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { onReply(review.id, replyText); setShowReplyBox(false); }}
                  disabled={replyText.length < 2}
                  className="px-3 py-1.5 rounded-md bg-[#7C8B6E] hover:bg-[#5A6A4D] text-white text-[12px] font-semibold disabled:opacity-50"
                >
                  Pubblica risposta
                </button>
                <button onClick={() => setShowReplyBox(false)} className="text-[12px] text-[#6B6A65] underline">Annulla</button>
              </div>
            </div>
          )}
          {showFlagBox && (
            <div className="mt-2 space-y-2">
              <textarea
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                placeholder="Perché la segnali? (es. insulti, IP duplicato, contenuto palesemente falso). Il founder rivede entro 24h."
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 text-[13px] border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#E11D48] bg-white"
                data-testid={`review-flag-text-${review.id}`}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { onFlag(review.id, flagReason); setShowFlagBox(false); }}
                  disabled={flagReason.length < 5}
                  className="px-3 py-1.5 rounded-md bg-[#E11D48] hover:bg-[#BE123C] text-white text-[12px] font-semibold disabled:opacity-50"
                >
                  Invia segnalazione
                </button>
                <button onClick={() => setShowFlagBox(false)} className="text-[12px] text-[#6B6A65] underline">Annulla</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
